import { createReducer, on } from '@ngrx/store';

import { SurveyCreationActions } from '../audience/survey-creation-audience.actions';
import {
  INITIAL_INSIGHTS_STATE,
  INITIAL_SURVEY_CREATION_PRICING_STATE,
} from './survey-creation-insights-state.constants';
import { InsightsState } from './survey-creation-insights-state.model';
import {
  AdvancedFilterActions,
  AdvancedFilterApiActions,
  SurveyCreationInsightsActions,
  SurveyCreationInsightsApiActions,
} from './survey-creation-insights.actions';

export const insightsReducer = createReducer(
  INITIAL_INSIGHTS_STATE,

  on(
    SurveyCreationInsightsApiActions.computeScorePending,
    (state): InsightsState => ({
      ...state,
      score: {
        ...state.score,
        loading: true,
        error: null,
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.computeScoreSuccess,
    (state, { scorePercentage }): InsightsState => ({
      ...state,
      score: {
        scorePercentage,
        loading: false,
        error: null,
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.computeScoreFailure,
    (state, { error }): InsightsState => ({
      ...state,
      score: {
        scorePercentage: null,
        loading: false,
        error,
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.recalculateFeasibilityPending,
    (state): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        loading: true,
        error: null,
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.recalculateFeasibilitySuccess,
    (
      state,
      { total, quotaGroups, audience, cpi, suppliers },
    ): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        total,
        quotaGroups,
        audience,
        cpi,
        suppliers,
        loading: false,
        error: null,
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.recalculateFeasibilityFailure,
    (state, { error }): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        loading: false,
        error,
      },
    }),
  ),

  on(AdvancedFilterActions.clearSurveyOptions, (state): InsightsState => {
    const lookup = state.feasibility.advancedFiltersLookup;

    return {
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup: {
          ...lookup,
          criteriaSurveyOptions: [],
          groupSurveyOptions: [],
          error: null,
        },
      },
    };
  }),

  on(
    AdvancedFilterActions.resetFilters,
    (state): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup:
          INITIAL_INSIGHTS_STATE.feasibility.advancedFiltersLookup,
      },
    }),
  ),

  on(
    AdvancedFilterApiActions.findSurveysSuccess,
    (state, { surveys, psidRefsBySurveyId }): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup: {
          ...state.feasibility.advancedFiltersLookup,
          matchedGroups: surveys,
          psidRefsBySurveyId: {
            ...state.feasibility.advancedFiltersLookup.psidRefsBySurveyId,
            ...psidRefsBySurveyId,
          },
          error: null,
        },
      },
    }),
  ),

  on(
    AdvancedFilterApiActions.findSurveysFailure,
    (state, { error }): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup: {
          ...state.feasibility.advancedFiltersLookup,
          error,
        },
      },
    }),
  ),

  on(
    AdvancedFilterApiActions.respondentCountsSuccess,
    (state, { totalCount }): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup: {
          ...state.feasibility.advancedFiltersLookup,
          respondentCount: totalCount,
          error: null,
        },
      },
    }),
  ),

  on(
    AdvancedFilterApiActions.respondentCountsFailure,
    (state, { error }): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup: {
          ...state.feasibility.advancedFiltersLookup,
          respondentCount: 0,
          error,
        },
      },
    }),
  ),

  on(
    AdvancedFilterApiActions.searchCriteriaSurveysSuccess,
    (state, { surveys }): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup: {
          ...state.feasibility.advancedFiltersLookup,
          criteriaSurveyOptions: surveys,
          error: null,
        },
      },
    }),
  ),

  on(
    AdvancedFilterApiActions.searchCriteriaSurveysFailure,
    (state, { error }): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup: {
          ...state.feasibility.advancedFiltersLookup,
          error,
        },
      },
    }),
  ),

  on(
    AdvancedFilterApiActions.searchGroupSurveysSuccess,
    (state, { surveys, psidRefsBySurveyId }): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup: {
          ...state.feasibility.advancedFiltersLookup,
          groupSurveyOptions: surveys,
          psidRefsBySurveyId: {
            ...state.feasibility.advancedFiltersLookup.psidRefsBySurveyId,
            ...psidRefsBySurveyId,
          },
          error: null,
        },
      },
    }),
  ),

  on(
    AdvancedFilterApiActions.searchGroupSurveysFailure,
    (state, { error }): InsightsState => ({
      ...state,
      feasibility: {
        ...state.feasibility,
        advancedFiltersLookup: {
          ...state.feasibility.advancedFiltersLookup,
          error,
        },
      },
    }),
  ),

  // Also hydrates pricing.offerPrice/surveyPauseThreshold from these prices —
  // this fires on survey load and every recalculation, so a save made without
  // ever opening the CPI drawer still carries the survey's existing pricing
  // instead of wiping it with `null`. Apply overwrites it afterwards.
  on(
    SurveyCreationInsightsApiActions.loadFlexibilityPricesSuccess,
    (state, { prices }): InsightsState => ({
      ...state,
      flexibilityPrices: {
        prices,
        error: null,
      },
      pricing: {
        ...state.pricing,
        offerPrice: prices.surveyPriceOverride,
        surveyPauseThreshold: prices.surveyPauseThreshold,
      },
    }),
  ),

  on(
    SurveyCreationInsightsActions.applyCpiPricing,
    (
      state,
      { offerPrice, surveyPauseThreshold, blendApplied },
    ): InsightsState => ({
      ...state,
      pricing: { offerPrice, surveyPauseThreshold, blendApplied },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.loadFlexibilityPricesFailure,
    (state, { error }): InsightsState => ({
      ...state,
      flexibilityPrices: {
        prices: null,
        error,
      },
      pricing: { ...INITIAL_SURVEY_CREATION_PRICING_STATE },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.loadSimilarSurveysPending,
    (state, { searchGlobally }): InsightsState => ({
      ...state,
      similarSurveys: {
        surveys: state.similarSurveys.surveys,
        status: 'loading',
        error: null,
        isStale: false,
        searchedTargetingKey: state.similarSurveys.searchedTargetingKey,
        searchedGlobally: searchGlobally,
        globalSearchUsed: state.similarSurveys.globalSearchUsed,
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.loadSimilarSurveysSuccess,
    (
      state,
      { surveys, searchGlobally, targetingKey, isStale },
    ): InsightsState => ({
      ...state,
      similarSurveys: {
        surveys,
        status: 'loaded',
        error: null,
        isStale,
        searchedTargetingKey: targetingKey,
        searchedGlobally: searchGlobally,
        globalSearchUsed:
          state.similarSurveys.globalSearchUsed || searchGlobally,
      },
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.loadSimilarSurveysFailure,
    (state, { error }): InsightsState => ({
      ...state,
      similarSurveys: {
        surveys: state.similarSurveys.surveys,
        status: 'failed',
        error,
        isStale: false,
        searchedTargetingKey: state.similarSurveys.searchedTargetingKey,
        searchedGlobally: state.similarSurveys.searchedGlobally,
        globalSearchUsed: state.similarSurveys.globalSearchUsed,
      },
    }),
  ),

  on(
    SurveyCreationInsightsActions.cancelSimilarSurveysSearch,
    (state): InsightsState => ({
      ...state,
      similarSurveys: {
        surveys: state.similarSurveys.surveys,
        status: state.similarSurveys.searchedTargetingKey ? 'loaded' : 'idle',
        error: null,
        isStale: !!state.similarSurveys.searchedTargetingKey,
        searchedTargetingKey: state.similarSurveys.searchedTargetingKey,
        searchedGlobally: state.similarSurveys.searchedGlobally,
        globalSearchUsed: state.similarSurveys.globalSearchUsed,
      },
    }),
  ),

  on(
    SurveyCreationInsightsActions.markSimilarSurveysStale,
    (state): InsightsState => ({
      ...state,
      similarSurveys: {
        surveys: state.similarSurveys.surveys,
        status: state.similarSurveys.status,
        error: state.similarSurveys.error,
        isStale: true,
        searchedTargetingKey: state.similarSurveys.searchedTargetingKey,
        searchedGlobally: state.similarSurveys.searchedGlobally,
        globalSearchUsed: state.similarSurveys.globalSearchUsed,
      },
    }),
  ),

  on(SurveyCreationActions.reset, (): InsightsState => INITIAL_INSIGHTS_STATE),
);
